/**
 * The interview link's own editor, in a modal, on the request card.
 *
 * ── Why it lives here ────────────────────────────────────────────────────────────────────────
 * The scheduling box's advice is almost always "widen the window or swap the interviewer in
 * Interview Links" — which meant leaving the tab, finding the link among all the others, fixing it,
 * and coming back to a queue that had meanwhile reloaded and lost which card you were on. This is
 * the same `LinkForm` the Interview Links page uses, loaded with the same link, saved through the
 * same route: one editor, one validation, no second implementation to drift.
 *
 * Deliberately a MODAL and not navigation: the recruiter is mid-reply, and the draft they were
 * editing has to still be there afterwards.
 */
import { useEffect, useState } from 'react';
import { Modal, ModalContent } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { LinkForm } from '../interviewLinks/LinkForm';
import { toDraft } from '../interviewLinks/linkDraft';
import { useRefreshAfterLinkChange } from '../interviewLinks/useRefreshAfterLinkChange';
import {
  fetchInterviewLinks,
  updateInterviewLink,
  type InterviewLink,
  type InterviewLinkDraft,
} from '../../services/interviewLinksApi';

export function LinkEditorModal({
  linkId,
  open,
  onClose,
  onSaved,
}: {
  linkId: string;
  open: boolean;
  onClose: () => void;
  /** The card re-reads its scheduling state, so a widened window shows up immediately. */
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<InterviewLinkDraft | null>(null);
  const [links, setLinks] = useState<InterviewLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshAfterLinkChange = useRefreshAfterLinkChange();

  useEffect(() => {
    if (!open) return;
    let alive = true;
    /**
     * The state updates are deferred into the promise chain rather than run synchronously in the
     * effect (the lint rule is about cascading renders), and `alive` drops a response that lands
     * after the modal closed — the recruiter can close it mid-fetch.
     */
    const start = Promise.resolve().then(() => {
      if (!alive) return null;
      setLoading(true);
      setError(null);
      return null;
    });
    void start;
    fetchInterviewLinks()
      .then((all) => {
        if (!alive) return;
        setLinks(all);
        const found = all.find((l) => l.id === linkId);
        if (!found) {
          // The link was deleted, or belongs to a role this user cannot see. Say which, rather than
          // rendering an empty form that would save as something new.
          setError('That interview link could not be loaded — open Interview Links to check it.');
          return;
        }
        setDraft(toDraft(found));
      })
      .catch(() => alive && setError('Could not load the interview link just now.'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, linkId]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      await updateInterviewLink(linkId, draft);
      // Same event the Interview Links page emits, so link edits made from a request card count too.
      void logAnalyticsEvent('interview_link_updated', { surface: 'requests' });
      showToast({ status: 'success', description: 'Interview link updated.' });
      // Same invalidations as the Interview Links page — without this, the board's cached payload
      // kept saying "no interview link" for a link repaired right here (Aug 2026).
      refreshAfterLinkChange();
      // Re-read BEFORE closing: the card's "no free slot" message is computed from the calendars,
      // so the recruiter should see it change rather than wonder whether the save took.
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the interview link.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent
        className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,52rem)] gap-6 overflow-auto rounded p-6 md:p-10"
        data-no-toggle
      >
        <h2 className="text-lg font-semibold">Edit interview link</h2>
        {loading && <p className="p-4 text-sm text-gray-500">Loading the link…</p>}
        {error && !draft && <p className="p-4 text-sm text-red-700">{error}</p>}
        {draft && (
          <LinkForm
            draft={draft}
            onChange={setDraft}
            onSubmit={() => void save()}
            onCancel={onClose}
            saving={saving}
            error={error}
            isEdit
            existingLinks={links}
          />
        )}
      </ModalContent>
    </Modal>
  );
}
